import { adjustEndDate, areOnSameDay } from "@/utils/DateHelper";
import { firestore } from "firebase";
import { ClientPanelAssignment } from "./ClientPanelAssignment";
import { ExternalElectricityMeterInfo, GenericDeviceProjectAssignment } from "./GenericDeviceAssignment";

export interface DeviceProjectAssignment {
  id: string;
  assignmentStartDate: firestore.Timestamp;
  assignmentEndDate: firestore.Timestamp | null;
  assignedProjectId: string;
  assignedProjectName: string;
  assignedProjectNumber: string;
  assignmentIndex: number;
  contactPersonId: string;
  /**
   * Room the device is assigned to under room-level device assignment (K7), always written next to
   * `contactPersonId`. Null or absent means the assignment is scoped to the apartment: legacy data,
   * Strombezug apartments, ERP-created assignments and projects with the setting off. Only the id is
   * stored; the room number is display data resolved from `damagePatternData.rooms`.
   */
  roomId?: string | null;
  electricityMeterForOtherDevices: boolean;
  externalElectricityMeterInfo: ExternalElectricityMeterInfo | null;
  electricityMeterNumber?: string | null;
  note?: string | null;
}

export function isClientPanelDeviceProjectAssignment(
  assignment: DeviceProjectAssignment
): assignment is ClientPanelAssignment {
  const serialNumber = (assignment as ClientPanelAssignment).serialNumber;

  return typeof serialNumber === "string" && serialNumber.trim().length > 0;
}

/**
 * Filters out the device project assignments that do not have external electricity meter information.
 *
 * @param {DeviceProjectAssignment[]} deviceProjectAssignments - The list of device project assignments to filter.
 * @returns {DeviceProjectAssignment[]} The filtered list of independent device project assignments.
 */
export function getIndependantDeviceProjectAssignments(
  deviceProjectAssignments: DeviceProjectAssignment[]
) {
  return deviceProjectAssignments.filter((assignment) => !assignment.externalElectricityMeterInfo);
}

/**
 * Filters out the device project assignments that have external electricity meter information.
 *
 * @param {DeviceProjectAssignment[]} deviceProjectAssignments - The list of device project assignments to filter.
 * @returns {DeviceProjectAssignment[]} The filtered list of dependent device project assignments.
 */
export function getDependantDeviceProjectAssignments(
  deviceProjectAssignments: DeviceProjectAssignment[]
) {
  return deviceProjectAssignments.filter((assignment) => assignment.externalElectricityMeterInfo !== null);
}

/**
 * Compares two device project assignments for sorting.
 *
 * The comparison is based on assignment start date, end date, and device name.
 * Open assignments (without an end date) are considered greater than closed ones.
 *
 * @param {DeviceProjectAssignment} left - The left device project assignment to compare.
 * @param {DeviceProjectAssignment} right - The right device project assignment to compare.
 * @returns {number} Returns -1 if left should be before right, 1 if left should be after right, and 0 if they are equal.
 */
export function compareDeviceProjectAssignments(
  left: DeviceProjectAssignment,
  right: DeviceProjectAssignment
) {
  const NOT_SWAPPED = -1;
  const EQUAL = 0;
  const SWAPPED = 1;

  const isLeftClientPanelAssignment = isClientPanelDeviceProjectAssignment(left);
  const isRightClientPanelAssignment = isClientPanelDeviceProjectAssignment(right);

  const leftDeviceName = isLeftClientPanelAssignment
    ? (left as ClientPanelAssignment).inventoryNumber ?? (left as ClientPanelAssignment).serialNumber
    : (left as GenericDeviceProjectAssignment).assignedDeviceInventoryNumber ?? "";

  const rightDeviceName = isRightClientPanelAssignment
    ? (right as ClientPanelAssignment).inventoryNumber ?? (right as ClientPanelAssignment).serialNumber
    : (right as GenericDeviceProjectAssignment).assignedDeviceInventoryNumber ?? "";

  const isLeftOpenAssignment = !left.assignmentEndDate;
  const isRightOpenAssignment = !right.assignmentEndDate;

  const startedOnSameDay = areOnSameDay(
    left.assignmentStartDate,
    right.assignmentStartDate
  );

  if (
    isLeftOpenAssignment && 
    !isRightOpenAssignment
  ) {
    return NOT_SWAPPED;
  }
  else if (
    isRightOpenAssignment && 
    !isLeftOpenAssignment
  ) {
    return SWAPPED;
  }

  if (!startedOnSameDay) {
    const shouldBeSwapped = right.assignmentStartDate.toMillis() - left.assignmentStartDate.toMillis() > EQUAL;

    return (
      shouldBeSwapped
        ? SWAPPED
        : NOT_SWAPPED
    );
  }

  const endedOnSameDay = areOnSameDay(
    adjustEndDate(left.assignmentEndDate),
    adjustEndDate(right.assignmentEndDate)
  );

  const shouldBeSwapped = 
    (
      !endedOnSameDay &&
      adjustEndDate(right.assignmentEndDate).toMillis() - adjustEndDate(left.assignmentEndDate).toMillis() > EQUAL
    ) ||
    (
      endedOnSameDay &&
      leftDeviceName.localeCompare(rightDeviceName) === SWAPPED
    );

  return (
    shouldBeSwapped
      ? SWAPPED
      : NOT_SWAPPED
  );
}
